import type { MidiFileNote, MidiFileTrack } from '@/util/midiFile';

/**
 * What a MIDI file says beyond the notes, and what becomes of it.
 *
 * A file carries a performance as well as a score: volume rides, pan moves,
 * bends, patch changes. Reading the score and discarding the rest imports a
 * flat version of what somebody played, and until now it did so silently.
 *
 * ## What finds a home, and what does not
 *
 * Two controllers map onto something this project actually has:
 *
 * - **CC7, channel volume**, and **CC11, expression** → the track's volume
 *   automation. They are two controls over one thing — the convention is that
 *   the sounding level is their product — so they become one lane holding that
 *   product. Keeping only one of them would be arbitrary, and CC11 is what most
 *   expressive writing rides.
 * - **CC10, pan** → the track's pan automation.
 *
 * And **pitch bend** → the synth's `bend` parameter, in semitones, scaled by
 * whatever bend range the file declared through registered parameter 0. The
 * synth shares one bend across its voices, which is what a bend is: one hand on
 * one wheel moving everything that is sounding.
 *
 * And **CC64, the sustain pedal**, which is not a parameter at all but a fact
 * about the notes: one released while the pedal is down keeps sounding until
 * the pedal lifts. The import lengthens the notes to say so, which is what
 * every sequencer does with a pedal on the way in. Without it a pedalled piano
 * passage arrived as a run of clipped notes, and nothing in the warnings said
 * why.
 *
 * Everything else is **reported rather than invented**:
 *
 * - **Program change** selects a patch from a library this project does not
 *   have. Three instruments cannot stand in for a hundred and twenty-eight, and
 *   picking one would be a guess presented as a reading of the file.
 * - **Every other controller** — filter cutoff, breath, the rest — would
 *   need a mapping from a controller number to a parameter, which is a
 *   decision per instrument rather than a fact about MIDI. Modulation was one
 *   of these until the instruments grew a mod wheel of their own; now CC1 is
 *   that parameter, and lands as a lane the way bend does.
 *
 * Saying so is the point. A warning that names what was in the file and what
 * happened to it is worth more than a faithful-looking import that quietly is
 * not.
 */

/**
 * The track's notes with the sustain pedal applied.
 *
 * A note released while the pedal is down ends when the pedal next lifts —
 * or, if it never lifts, where the last note of the track ends, because a
 * player would have kept sounding it until the file ran out. A held note never
 * runs into the next strike of the same pitch on its channel: a real damper
 * lifts for a restrike, and a synth handed two overlapping notes at one pitch
 * cannot tell which release belongs to which. Notes released with the pedal
 * up are untouched, so a track with no pedal comes back exactly as it went in.
 */
export function holdPedalledNotes(track: MidiFileTrack): MidiFileNote[] {
  const pedal = track.controllers
    .filter((event) => event.controller === CC_SUSTAIN)
    .sort((a, b) => a.tick - b.tick);
  if (pedal.length === 0) return track.notes;

  // Where the pedal lifts, per channel, in order. A lift is the first event at
  // or after a moment that reads below the threshold while the pedal was down.
  const liftsByChannel = new Map<number, number[]>();
  const downByChannel = new Map<number, boolean>();
  for (const event of pedal) {
    const down = event.value >= SUSTAIN_DOWN_FROM;
    const wasDown = downByChannel.get(event.channel) ?? false;
    if (wasDown && !down) {
      const lifts = liftsByChannel.get(event.channel) ?? [];
      lifts.push(event.tick);
      liftsByChannel.set(event.channel, lifts);
    }
    downByChannel.set(event.channel, down);
  }

  const trackEnd = track.notes.reduce((end, note) => Math.max(end, note.startTicks + note.durationTicks), 0);

  /** Whether the pedal is down at `tick` on a channel, and when it next lifts. */
  const pedalAt = (channel: number, tick: number): { down: boolean; liftTick: number } => {
    let down = false;
    for (const event of pedal) {
      if (event.channel !== channel) continue;
      if (event.tick > tick) break;
      down = event.value >= SUSTAIN_DOWN_FROM;
    }
    if (!down) return { down: false, liftTick: tick };
    const lift = (liftsByChannel.get(channel) ?? []).find((at) => at > tick);
    return { down: true, liftTick: lift ?? Math.max(tick, trackEnd) };
  };

  const sorted = [...track.notes].sort((a, b) => a.startTicks - b.startTicks);
  return track.notes.map((note) => {
    const release = note.startTicks + note.durationTicks;
    const { down, liftTick } = pedalAt(note.channel, release);
    if (!down || liftTick <= release) return note;
    // Not past the next strike of the same pitch on this channel.
    const restrike = sorted.find(
      (other) => other.channel === note.channel && other.pitch === note.pitch && other.startTicks >= release && other !== note,
    );
    const end = restrike ? Math.min(liftTick, restrike.startTicks) : liftTick;
    return end > release ? { ...note, durationTicks: end - note.startTicks } : note;
  });
}

/** How many of a track's notes the pedal lengthened. */
export function pedalledNoteCount(track: MidiFileTrack): number {
  const held = holdPedalledNotes(track);
  return held.filter((note, index) => note !== track.notes[index]).length;
}

/** MIDI controller numbers this project can act on. */
export const CC_MOD_WHEEL = 1;
export const CC_VOLUME = 7;
export const CC_PAN = 10;
export const CC_EXPRESSION = 11;
export const CC_SUSTAIN = 64;

/** The pedal is down from this value up; the spec draws the line at the middle. */
export const SUSTAIN_DOWN_FROM = 64;

/**
 * The controllers that carry a registered parameter, and the one that matters.
 *
 * A file declares its bend range by selecting registered parameter 0 — RPN MSB
 * and LSB both zero — and then writing the value as semitones in the data-entry
 * MSB and cents in its LSB. It is a small state machine rather than a single
 * message, which is why the range has to be walked for rather than read.
 */
export const CC_DATA_ENTRY_MSB = 6;
export const CC_DATA_ENTRY_LSB = 38;
export const CC_RPN_LSB = 100;
export const CC_RPN_MSB = 101;

/** What a file means by a fully deflected wheel when it does not say. */
export const DEFAULT_BEND_RANGE_SEMITONES = 2;

/** A point destined for an automation lane, in the file's own ticks. */
export interface PerformancePoint {
  tick: number;
  value: number;
}

export interface PerformanceLanes {
  /** Track volume, as linear gain. Empty when the file rides neither level. */
  volume: PerformancePoint[];
  /** Track pan, -1 to 1. Empty when the file never pans. */
  pan: PerformancePoint[];
}

/**
 * How many semitones a fully deflected bend wheel means, for this track.
 *
 * Two semitones unless the file says otherwise, which is the convention every
 * synth and every sequencer has followed since MIDI 1.0. A file that wants an
 * octave says so, and reading it wrong means importing a bend that is six times
 * too small — audibly wrong in a way that looks like a bug in the bend rather
 * than in the reading of it.
 *
 * Only the first declaration is taken. A file that changes its bend range
 * part-way through is doing something this project cannot represent with one
 * automation lane, and quietly honouring the last one would make the earlier
 * bends wrong instead.
 */
export function bendRangeSemitones(track: MidiFileTrack): number {
  let selected = false;
  let semitones: number | null = null;
  let cents = 0;
  let done = false;

  for (const event of [...track.controllers].sort((a, b) => a.tick - b.tick)) {
    if (event.controller === CC_RPN_MSB) {
      // Selecting any registered parameter other than 0, or deselecting with
      // 127, means the data entry that follows is not about bend.
      selected = event.value === 0;
      continue;
    }
    if (event.controller === CC_RPN_LSB) {
      selected = selected && event.value === 0;
      continue;
    }
    if (!selected || done) continue;

    if (event.controller === CC_DATA_ENTRY_MSB) {
      // A second coarse value is a second declaration, and the first one wins.
      // The cents that go with the first arrive *after* it, so this cannot be a
      // simple "stop once we have a number" — that would read the semitones and
      // throw away the cents belonging to them.
      if (semitones !== null) done = true;
      else semitones = event.value;
    } else if (event.controller === CC_DATA_ENTRY_LSB) {
      cents = event.value;
    }
  }

  if (semitones === null) return DEFAULT_BEND_RANGE_SEMITONES;
  return Math.max(0, Math.min(24, semitones + cents / 100));
}

/**
 * A track's pitch bend, in semitones, ready for a `bend` automation lane.
 *
 * The wire value is a fraction of the range either way, so this is the one
 * place the declared range is applied — everything downstream counts semitones.
 */
export function bendPoints(track: MidiFileTrack): PerformancePoint[] {
  const range = bendRangeSemitones(track);
  return dedupe(
    [...track.pitchBends]
      .sort((a, b) => a.tick - b.tick)
      .map((event) => ({ tick: event.tick, value: event.value * range })),
  );
}

/**
 * A track's modulation, ready for a `modWheel` lane: the wheel from 0 to 1,
 * which is the parameter's own range.
 */
export function modWheelPoints(track: MidiFileTrack): PerformancePoint[] {
  return dedupe(
    track.controllers
      .filter((event) => event.controller === CC_MOD_WHEEL)
      .sort((a, b) => a.tick - b.tick)
      .map((event) => ({ tick: event.tick, value: Math.min(127, Math.max(0, event.value)) / 127 })),
  );
}

/**
 * The gain a level controller asks for.
 *
 * MIDI level controls are defined as amplitude proportional to the square of the
 * value, not to the value: General MIDI 2 and DLS both specify it, and it is why
 * a fader at halfway sounds like a fader at halfway rather than like -6dB. At
 * 127 it is exactly unity, which is what makes an unridden file import at the
 * level it was written.
 */
export function levelGain(value: number): number {
  const clamped = Math.min(127, Math.max(0, value)) / 127;
  return clamped * clamped;
}

/**
 * Pan, from a controller that has no exact centre.
 *
 * 64 is the defined centre of a 0-127 range, which leaves 64 values below it and
 * 63 above. Dividing each side by its own span puts the centre exactly at zero
 * and both extremes exactly at the ends, which matters more than the two halves
 * having the same resolution — that is the format's problem, and pretending
 * otherwise would put a file's centre slightly off centre.
 */
export function panPosition(value: number): number {
  const clamped = Math.min(127, Math.max(0, value));
  return clamped <= 64 ? (clamped - 64) / 64 : (clamped - 64) / 63;
}

/** The controller value for a gain: the level law backwards, held to the unity a controller can say. */
export function levelController(gain: number): number {
  return Math.max(0, Math.min(127, Math.round(Math.sqrt(Math.max(0, gain)) * 127)));
}

/** The controller value for a pan position: the pan formula backwards, lopsided centre and all. */
export function panController(position: number): number {
  const clamped = Math.max(-1, Math.min(1, position));
  return Math.max(0, Math.min(127, Math.round(64 + (clamped <= 0 ? clamped * 64 : clamped * 63))));
}

/**
 * Turns a track's controllers into lane points.
 *
 * Volume and expression are combined by walking both in one pass and multiplying
 * the last value seen of each, so a point lands wherever either of them moves.
 * Doing it any other way — resampling onto a grid, or taking whichever arrived
 * last — would either invent points the file does not have or lose one control
 * whenever the other moved.
 *
 * Both start at 127, which is what a channel sounds at when a file never
 * mentions the controller at all. So a file that rides only expression starts at
 * unity rather than at silence.
 */
export function performanceLanes(track: MidiFileTrack): PerformanceLanes {
  const levels = track.controllers
    .filter((e) => e.controller === CC_VOLUME || e.controller === CC_EXPRESSION)
    .sort((a, b) => a.tick - b.tick);

  const volume: PerformancePoint[] = [];
  let cc7 = 127;
  let cc11 = 127;
  for (const event of levels) {
    if (event.controller === CC_VOLUME) cc7 = event.value;
    else cc11 = event.value;
    const value = levelGain(cc7) * levelGain(cc11);
    const last = volume[volume.length - 1];
    // Two controllers can land on one tick — a volume and an expression written
    // together — and two points at one position is one point.
    if (last && last.tick === event.tick) last.value = value;
    else volume.push({ tick: event.tick, value });
  }

  const pan = track.controllers
    .filter((e) => e.controller === CC_PAN)
    .sort((a, b) => a.tick - b.tick)
    .map((e) => ({ tick: e.tick, value: panPosition(e.value) }));

  return { volume, pan: dedupe(pan) };
}

function dedupe(points: PerformancePoint[]): PerformancePoint[] {
  const out: PerformancePoint[] = [];
  for (const point of points) {
    const last = out[out.length - 1];
    if (last && last.tick === point.tick) last.value = point.value;
    else out.push(point);
  }
  return out;
}

/**
 * What was in the file that no lane could take, in words.
 *
 * One line per kind, naming the count, so "your modulation is missing" is
 * something the import says rather than something the user works out later.
 */
export function unmappedPerformance(track: MidiFileTrack): string[] {
  const said: string[] = [];

  const handled = new Set([
    CC_MOD_WHEEL,
    CC_VOLUME,
    CC_PAN,
    CC_EXPRESSION,
    // Applied to the notes rather than carried as a lane.
    CC_SUSTAIN,
    // Not automation, but read and acted on: these four declared the bend range.
    CC_DATA_ENTRY_MSB,
    CC_DATA_ENTRY_LSB,
    CC_RPN_LSB,
    CC_RPN_MSB,
  ]);

  const others = new Map<number, number>();
  for (const event of track.controllers) {
    if (handled.has(event.controller)) continue;
    others.set(event.controller, (others.get(event.controller) ?? 0) + 1);
  }
  if (others.size > 0) {
    const numbers = [...others.keys()].sort((a, b) => a - b).join(', ');
    const total = [...others.values()].reduce((sum, n) => sum + n, 0);
    said.push(
      `${total} controller event(s) were not imported (CC ${numbers}). Only modulation, volume, expression and pan ` +
        'have a parameter here to land on.',
    );
  }

  if (track.programChanges.length > 0) {
    const programs = [...new Set(track.programChanges.map((e) => e.program))].sort((a, b) => a - b);
    said.push(
      `${track.programChanges.length} program change(s) were not imported (program ${programs.join(', ')}). ` +
        'Choosing an instrument for a General MIDI patch number would be a guess.',
    );
  }

  return said;
}
